import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../AdminPanel.css";
import SuperAdminSidebar from "../SuperAdminSidebar";
import ManagerSidebar from "../ManagerSidebar";
import LeaderSidebar from "../LeaderSidebar";
import MemberSidebar from "../MemberSidebar";
import AdminNavbar from './AdminNavbar';
import { Table, Form, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';

const Accounts = () => {
    const navigate = useNavigate();
    const [userType, setUserType] = useState("");
    const [userFullName, setUserFullName] = useState("");
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [balanceSheet, setBalanceSheet] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [clientLoading, setClientLoading] = useState(false);

    useEffect(() => {
        const storedUserType = localStorage.getItem("usertype");
        const storedFName = localStorage.getItem("fname");
        const storedLName = localStorage.getItem("lname");

        if (!storedUserType) {
            navigate("/login");
        } else {
            setUserType(storedUserType);
            if (storedFName && storedLName) {
                setUserFullName(`${storedFName} ${storedLName}`);
            } else {
                setUserFullName(storedFName || '');
            }
            fetchClients();
        }
    }, [navigate]);

    const fetchClients = async () => {
        try {
            setClientLoading(true);
            setError("");
            const response = await fetch('https://skcapi.btcjapan.co/clientAccount.php?action=getClients');

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                setClients(result.data || []);
            } else {
                setError(result.message || "Failed to fetch clients");
            }
        } catch (err) {
            setError("Failed to load clients. Please check the API endpoint.");
            console.error("Fetch clients error:", err);
        } finally {
            setClientLoading(false);
        }
    };

    const fetchBalanceSheet = async () => {
        if (!selectedClient) {
            setError("Please select a client first");
            return;
        }

        try {
            setLoading(true);
            setError("");
            setBalanceSheet([]);

            let url = new URL('https://skcapi.btcjapan.co/clientAccount.php');
            url.searchParams.append('action', 'getBalanceSheet');
            url.searchParams.append('clientId', selectedClient);
            if (dateFrom) url.searchParams.append('dateFrom', dateFrom);
            if (dateTo) url.searchParams.append('dateTo', dateTo);

            const response = await fetch(url.toString());

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                console.log("API Response:", result);
                setBalanceSheet(result.data || []);
                if (result.data.length === 0) {
                    setError("No records found for the selected criteria");
                }
            } else {
                setError(result.message || "Failed to fetch balance sheet");
            }
        } catch (err) {
            setError("Failed to connect to the server. Please try again later.");
            console.error("Fetch balance sheet error:", err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    };

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("email");
        localStorage.removeItem("userName");
        localStorage.removeItem("usertype");
        localStorage.removeItem("fname");
        localStorage.removeItem("lname");
        navigate("/login");
    };

    let runningBalance = 0;
    const balanceSheetWithRunningBalance = balanceSheet.map((transaction) => {
        const received = parseFloat(transaction.received_amount || 0);
        const allocated = parseFloat(transaction.allocated_amount || 0);
        runningBalance = runningBalance + received - allocated;
        return { ...transaction, balance: runningBalance };
    });

    const totalBalance = balanceSheetWithRunningBalance.length > 0 
        ? balanceSheetWithRunningBalance[balanceSheetWithRunningBalance.length - 1].balance 
        : 0;

    const downloadExcel = () => {
        // Prepare data for Excel
        const excelData = balanceSheetWithRunningBalance.map((transaction, index) => ({
            '#': index + 1,
            Date: formatDate(transaction.date),
            Client: transaction.client_name,
            'Stock ID': transaction.stock_id,
            'Received Amount ($)': parseFloat(transaction.received_amount || 0),
            'Allocated Amount ($)': parseFloat(transaction.allocated_amount || 0),
            'Balance ($)': parseFloat(transaction.balance)
        }));

        // Add total row
        excelData.push({
            '#': '',
            Date: '',
            Client: '',
            'Stock ID': '',
            'Received Amount ($)': '',
            'Allocated Amount ($)': 'Total:',
            'Balance ($)': totalBalance
        });

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(excelData);
        // Create workbook and append the worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Balance Sheet");
        // Generate Excel file and trigger download
        const selectedClientName = clients.find(client => client.id === parseInt(selectedClient))?.username || 'Client';
        XLSX.writeFile(wb, `Balance_Sheet_${selectedClientName}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <div className="admin-container">
            <div className="sidebar-container">
              {userType === "administrator" && <SuperAdminSidebar />}
              {userType === "leader" && <LeaderSidebar />}
              {userType === "manager" && <ManagerSidebar />}
              {userType === "member" && <MemberSidebar />}
            </div>

            <div className="admin-content">
                {/* Use the new AdminNavbar component */}
                <AdminNavbar />

                <div className="main-section">
                    <h3 className='section-title'>Client Accounts</h3>

                    <div className="accounts-filter">
                        <Form>
                            <Row>
                                <Col md={4}>
                                    <Form.Group controlId="clientSelect">
                                        <Form.Label>Select Client</Form.Label>
                                        {clientLoading ? (
                                            <div className="text-center">
                                                <Spinner animation="border" size="sm" /> Loading clients...
                                            </div>
                                        ) : (
                                            <Form.Control
                                                as="select"
                                                value={selectedClient}
                                                onChange={(e) => setSelectedClient(e.target.value)}
                                                disabled={loading}
                                            >
                                                <option value="">Select a client</option>
                                                {clients.map(client => (
                                                    <option key={client.id} value={client.id}>
                                                        {client.fname} {client.lname} ({client.username})
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        )}
                                    </Form.Group>
                                </Col>

                                <Col md={3}>
                                    <Form.Group controlId="dateFrom">
                                        <Form.Label>From Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={dateFrom}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                            placeholder="mm/dd/yyyy"
                                            disabled={loading}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={3}>
                                    <Form.Group controlId="dateTo">
                                        <Form.Label>To Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={dateTo}
                                            onChange={(e) => setDateTo(e.target.value)}
                                            placeholder="mm/dd/yyyy"
                                            disabled={loading}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2} className="d-flex align-items-end">
                                    <Button
                                        variant="success"
                                        onClick={fetchBalanceSheet}
                                        disabled={loading || !selectedClient}
                                        style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner animation="border" size="sm" /> Searching...
                                            </>
                                        ) : 'Search'}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                    {error && (
                        <Alert variant={balanceSheet.length === 0 ? "info" : "danger"} className="mt-3">
                            {error}
                        </Alert>
                    )}

                    {balanceSheetWithRunningBalance.length > 0 && (
                        <div className="balance-sheet mt-4">
                            <div className="d-flex justify-content-end mb-2">
                                <Button
                                    variant="primary"
                                    onClick={downloadExcel}
                                    style={{ backgroundColor: '#007bff', borderColor: '#007bff' }}
                                >
                                    Download Excel
                                </Button>
                            </div>
                            <h5>Balance Sheet</h5>
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Date</th>
                                        <th>Client</th>
                                        <th>Stock ID</th>
                                        <th>Received Amount ($)</th>
                                        <th>Allocated Amount ($)</th>
                                        <th>Balance ($)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {balanceSheetWithRunningBalance.map((transaction, index) => (
                                        <tr key={transaction.id}>
                                            <td>{index + 1}</td>
                                            <td>{formatDate(transaction.date)}</td>
                                            <td>{transaction.client_name}</td>
                                            <td>{transaction.stock_id}</td>
                                            <td>${parseFloat(transaction.received_amount || 0).toLocaleString()}</td>
                                            <td>${parseFloat(transaction.allocated_amount || 0).toLocaleString()}</td>
                                            <td>${parseFloat(transaction.balance).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="6" className="text-end"><strong>Total:</strong></td>
                                        <td><strong>${totalBalance.toLocaleString()}</strong></td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Accounts;